import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  listCertRules, createCertRule, updateCertRule, deleteCertRule, previewCertRules,
  getCertRuleSuggestions,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Plus, Trash2, Pencil, ChevronLeft, Sparkles, Play, AlertCircle, Brain, ChevronRight,
  Lightbulb, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

const CONDITION_FIELDS = [
  { value: 'description', label: 'Circuit description' },
  { value: 'mcbType', label: 'MCB type (B/C/D)' },
  { value: 'mcbRating', label: 'MCB rating' },
  { value: 'rcdRating', label: 'RCD rating' },
  { value: 'bsEn', label: 'BS EN standard' },
  { value: 'circuitNumber', label: 'Circuit number' },
  { value: 'phases', label: 'Phases (board)' },
  { value: 'numberingScheme', label: 'Numbering scheme (board)' },
  { value: 'wayCount', label: 'Way count (board)' },
];

const ACTION_FIELDS = [
  { value: 'liveCableSize', label: 'Live cable size' },
  { value: 'neutralCableSize', label: 'Neutral cable size' },
  { value: 'cpcSize', label: 'CPC (earth) cable size' },
  { value: 'cableSize', label: 'Cable size (generic)' },
  { value: 'notes', label: 'Notes' },
];

const CONDITION_OPS = [
  { value: 'equals', label: 'equals' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'regex', label: 'matches regex' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'not_empty', label: 'is not empty' },
];

const ACTION_OPS = [
  { value: 'set', label: 'set to' },
  { value: 'set_template', label: 'set to template' },
];

const EMPTY_RULE = {
  name: '',
  enabled: true,
  priority: 100,
  scope: 'card',
  when: [{ field: 'mcbRating', op: 'equals', value: '' }],
  then: [{ field: 'liveCableSize', op: 'set', value: '' }],
};

const RuleRow = ({ rule, onEdit, onToggle, onDelete }) => (
  <div
    className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
      rule.enabled ? '' : 'opacity-50'
    }`}
    data-testid={`rule-row-${rule.id}`}
  >
    <div className="flex items-center gap-3">
      <Switch
        checked={rule.enabled}
        onCheckedChange={() => onToggle(rule)}
        data-testid={`rule-toggle-${rule.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 truncate">{rule.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          IF{' '}
          {(rule.when || []).map((c, i) => (
            <span key={i} className="font-mono text-slate-700">
              {i > 0 && ' AND '}{c.field} <span className="text-slate-400">{c.op}</span>{c.value ? ` "${c.value}"` : ''}
            </span>
          ))}
          {' '}THEN{' '}
          {(rule.then || []).map((a, i) => (
            <span key={i} className="font-mono text-emerald-700">
              {i > 0 && ', '}{a.field} <span className="text-slate-400">{a.op}</span> "{a.value}"
            </span>
          ))}
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5">
          scope: {rule.scope} · priority: {rule.priority} · created by {rule.createdBy}
        </div>
      </div>
      <Button variant="ghost" size="icon" onClick={() => onEdit(rule)} data-testid={`rule-edit-${rule.id}`}>
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onDelete(rule)}
        className="text-slate-400 hover:text-red-600"
        data-testid={`rule-delete-${rule.id}`}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

const ConditionRow = ({ value, onChange, onRemove, fields }) => (
  <div className="flex items-center gap-2">
    <Select value={value.field} onValueChange={v => onChange({ ...value, field: v })}>
      <SelectTrigger className="h-9 flex-1" data-testid="cond-field"><SelectValue /></SelectTrigger>
      <SelectContent>
        {fields.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
      </SelectContent>
    </Select>
    <Select value={value.op} onValueChange={v => onChange({ ...value, op: v })}>
      <SelectTrigger className="h-9 w-[140px]" data-testid="cond-op"><SelectValue /></SelectTrigger>
      <SelectContent>
        {CONDITION_OPS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
    {!['is_empty', 'not_empty'].includes(value.op) && (
      <Input
        value={value.value}
        onChange={e => onChange({ ...value, value: e.target.value })}
        placeholder="value"
        className="h-9 flex-1"
        data-testid="cond-value"
      />
    )}
    <Button variant="ghost" size="icon" onClick={onRemove} className="text-slate-400 hover:text-red-600">
      <Trash2 className="h-4 w-4" />
    </Button>
  </div>
);

const ActionRow = ({ value, onChange, onRemove }) => (
  <div className="flex items-center gap-2">
    <Select value={value.field} onValueChange={v => onChange({ ...value, field: v })}>
      <SelectTrigger className="h-9 flex-1" data-testid="action-field"><SelectValue /></SelectTrigger>
      <SelectContent>
        {ACTION_FIELDS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
      </SelectContent>
    </Select>
    <Select value={value.op} onValueChange={v => onChange({ ...value, op: v })}>
      <SelectTrigger className="h-9 w-[140px]" data-testid="action-op"><SelectValue /></SelectTrigger>
      <SelectContent>
        {ACTION_OPS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
    <Input
      value={value.value}
      onChange={e => onChange({ ...value, value: e.target.value })}
      placeholder={value.op === 'set_template' ? 'e.g. 2× {mcbRating} = 2.5mm²' : 'value'}
      className="h-9 flex-1 font-mono text-sm"
      data-testid="action-value"
    />
    <Button variant="ghost" size="icon" onClick={onRemove} className="text-slate-400 hover:text-red-600">
      <Trash2 className="h-4 w-4" />
    </Button>
  </div>
);

const RuleEditor = ({ open, onClose, initial, onSaved }) => {
  const [draft, setDraft] = useState(EMPTY_RULE);
  const [saving, setSaving] = useState(false);
  const [previewSample, setPreviewSample] = useState({
    description: 'KITCHEN RING',
    mcbType: 'B',
    mcbRating: '32A',
    rcdRating: '30mA',
    bsEn: '60898',
  });
  const [previewResult, setPreviewResult] = useState(null);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) {
      setDraft(initial?.id ? { ...initial } : EMPTY_RULE);
      setPreviewResult(null);
    }
  }, [open, initial]);

  const updWhen = (i, v) => setDraft(d => ({ ...d, when: d.when.map((x, idx) => idx === i ? v : x) }));
  const removeWhen = (i) => setDraft(d => ({ ...d, when: d.when.filter((_, idx) => idx !== i) }));
  const addWhen = () => setDraft(d => ({ ...d, when: [...(d.when || []), { field: 'description', op: 'contains', value: '' }] }));
  const updThen = (i, v) => setDraft(d => ({ ...d, then: d.then.map((x, idx) => idx === i ? v : x) }));
  const removeThen = (i) => setDraft(d => ({ ...d, then: d.then.filter((_, idx) => idx !== i) }));
  const addThen = () => setDraft(d => ({ ...d, then: [...(d.then || []), { field: 'liveCableSize', op: 'set', value: '' }] }));

  const runPreview = async () => {
    try {
      const r = await previewCertRules([draft], previewSample);
      setPreviewResult(r.derived);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    }
  };

  const save = async () => {
    if (!draft.name.trim()) return toast.error('Give the rule a name');
    if (!draft.then || draft.then.length === 0) return toast.error('Add at least one THEN action');
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        enabled: Boolean(draft.enabled),
        priority: Number(draft.priority || 100),
        scope: draft.scope || 'card',
        when: draft.when || [],
        then: draft.then || [],
      };
      const saved = isEdit
        ? await updateCertRule(draft.id, payload)
        : await createCertRule(payload);
      toast.success(isEdit ? 'Rule updated' : 'Rule created');
      onSaved(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit rule' : 'New rule'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input
                value={draft.name}
                onChange={e => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. 32A → 2.5mm² cables"
                className="mt-1.5"
                data-testid="rule-name-input"
              />
            </div>
            <div>
              <Label>Priority (lower runs first)</Label>
              <Input
                type="number"
                value={draft.priority}
                onChange={e => setDraft({ ...draft, priority: e.target.value })}
                className="mt-1.5"
                data-testid="rule-priority-input"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pb-1">
            <Switch
              checked={draft.enabled}
              onCheckedChange={v => setDraft({ ...draft, enabled: v })}
              data-testid="rule-enabled-toggle"
            />
            <Label className="cursor-pointer">Enabled</Label>
            <div className="ml-auto flex items-center gap-1.5">
              <Label className="text-xs">Scope</Label>
              <Select value={draft.scope} onValueChange={v => setDraft({ ...draft, scope: v })}>
                <SelectTrigger className="h-8 w-[140px]" data-testid="rule-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="card">Per circuit (card)</SelectItem>
                  <SelectItem value="board">Per board</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border border-slate-200 rounded-md p-4 bg-slate-50/40">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display uppercase tracking-wide text-slate-900 text-sm">IF (all of these match)</h4>
              <Button variant="ghost" size="sm" onClick={addWhen}>
                <Plus className="h-4 w-4 mr-1" /> Add condition
              </Button>
            </div>
            <div className="space-y-2">
              {(draft.when || []).map((c, i) => (
                <ConditionRow key={i} value={c} onChange={v => updWhen(i, v)} onRemove={() => removeWhen(i)} fields={CONDITION_FIELDS} />
              ))}
              {(!draft.when || draft.when.length === 0) && (
                <div className="text-xs text-slate-500 italic">
                  No conditions — rule will match every input. Add at least one condition unless that&apos;s really what you want.
                </div>
              )}
            </div>
          </div>

          <div className="border border-emerald-200 rounded-md p-4 bg-emerald-50/40">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display uppercase tracking-wide text-emerald-900 text-sm">THEN (apply these)</h4>
              <Button variant="ghost" size="sm" onClick={addThen}>
                <Plus className="h-4 w-4 mr-1" /> Add action
              </Button>
            </div>
            <div className="space-y-2">
              {(draft.then || []).map((a, i) => (
                <ActionRow key={i} value={a} onChange={v => updThen(i, v)} onRemove={() => removeThen(i)} />
              ))}
            </div>
            <div className="mt-2 text-[11px] text-slate-600">
              Tip: with <code className="bg-white px-1 rounded">set to template</code> you can reference other fields like <code className="bg-white px-1 rounded">{'{mcbRating}'}</code> — useful for ring-circuit notation like <code className="bg-white px-1 rounded">2× {'{mcbRating}'} = 2.5mm²</code>.
            </div>
          </div>

          <div className="border border-blue-200 rounded-md p-4 bg-blue-50/40">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display uppercase tracking-wide text-blue-900 text-sm">Sandbox preview</h4>
              <Button variant="outline" size="sm" onClick={runPreview} data-testid="preview-btn">
                <Play className="h-4 w-4 mr-1" /> Test rule
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {Object.entries(previewSample).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5">
                  <Label className="text-[10px] w-24">{k}</Label>
                  <Input
                    value={v}
                    onChange={e => setPreviewSample({ ...previewSample, [k]: e.target.value })}
                    className="h-7 text-xs"
                  />
                </div>
              ))}
            </div>
            {previewResult && (
              <div className="mt-3 p-2 bg-white border border-slate-200 rounded text-xs" data-testid="preview-result">
                {Object.entries(previewResult).filter(([k]) => k !== '_matchedRules').length === 0 ? (
                  <div className="text-slate-500 italic">No fields derived — conditions didn&apos;t match the sample input.</div>
                ) : (
                  <>
                    <div className="font-semibold text-slate-700 mb-1">Derived fields:</div>
                    {Object.entries(previewResult).filter(([k]) => k !== '_matchedRules').map(([k, v]) => (
                      <div key={k} className="font-mono">
                        <span className="text-slate-500">{k}</span> → <span className="text-emerald-700">{v}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="rule-save-btn"
          >
            {isEdit ? 'Save changes' : 'Create rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CertRules = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const base = location.pathname.startsWith('/employee') ? '/employee' : '/admin';
  const [rules, setRules] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rulesRes, sugRes] = await Promise.all([
        listCertRules(),
        getCertRuleSuggestions(3).catch(() => ({ suggestions: [] })),
      ]);
      setRules(rulesRes.rules || []);
      setSuggestions(sugRes.suggestions || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (rule) => {
    try {
      await updateCertRule(rule.id, { ...rule, enabled: !rule.enabled });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not toggle');
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? This can't be undone.`)) return;
    try {
      await deleteCertRule(rule.id);
      toast.success('Rule deleted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleAcceptSuggestion = (sug) => {
    // Pre-fill the editor with the suggested rule and let the engineer
    // tweak before saving. Once saved, the suggestion will naturally
    // disappear from the list on the next reload because the rule's
    // signature now matches an existing enabled rule.
    setEditing(sug.suggestedRule);
    setEditorOpen(true);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Cert Rules"
        description="Author IF/THEN rules that derive secondary fields from the AI's primary extractions. Engineers see derivations live on the Training game and (soon) the real Certificate form."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`${base}/certificates/train`)}>
              <Brain className="h-4 w-4 mr-1" /> Training game
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`${base}/certificates`)}
              data-testid="back-to-certs-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Certificates
            </Button>
          </div>
        }
      />

      {suggestions.length > 0 && (
        <section
          className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-md overflow-hidden"
          data-testid="suggestions-panel"
        >
          <div className="px-5 py-3 border-b border-amber-200/60 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-700" />
              <h2 className="font-display uppercase tracking-wide text-amber-900 text-sm">
                Suggested rules
              </h2>
              <span className="text-[11px] text-amber-700 px-2 py-0.5 rounded-full bg-amber-100">
                {suggestions.length} pattern{suggestions.length === 1 ? '' : 's'} detected
              </span>
            </div>
            <span className="text-[11px] text-amber-700">
              Recurring corrections from the AI accuracy data — tap one to turn it into a rule.
            </span>
          </div>
          <div className="divide-y divide-amber-100">
            {suggestions.map(sug => (
              <div
                key={sug.id}
                className="px-5 py-3 hover:bg-amber-50/50 transition-colors flex items-center gap-3 flex-wrap"
                data-testid={`suggestion-${sug.id}`}
              >
                <TrendingUp className="h-4 w-4 text-amber-700 shrink-0" />
                <div className="flex-1 min-w-0 text-sm text-amber-900">
                  <div className="font-medium">{sug.message}</div>
                  <div className="text-xs text-amber-700 mt-0.5 font-mono">
                    {sug.field} : <span className="line-through">{sug.aiValue}</span> → <span className="font-semibold">{sug.finalValue}</span>
                    <span className="ml-2 text-amber-600">×{sug.count}</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleAcceptSuggestion(sug)}
                  className="bg-amber-700 hover:bg-amber-800 text-white"
                  data-testid={`accept-suggestion-${sug.id}`}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Create rule
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/60 flex items-center justify-between">
          <h2 className="font-display uppercase tracking-wide text-slate-900 text-sm">All rules</h2>
          <Button
            onClick={() => { setEditing(null); setEditorOpen(true); }}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="new-rule-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New rule
          </Button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : rules.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-rules">
            <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <Sparkles className="h-6 w-6 text-slate-400" />
            </div>
            <h3 className="font-display uppercase tracking-wide text-slate-900 mb-1">No rules yet</h3>
            <p className="text-sm text-slate-600 max-w-md mx-auto mb-4">
              Rules let you teach the system shortcuts — e.g. <span className="font-mono">if mcbRating equals 32A then liveCableSize set 2.5mm²</span>.
              Hit New rule to get started.
            </p>
            <Button
              onClick={() => { setEditing(null); setEditorOpen(true); }}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              <Plus className="h-4 w-4 mr-1" /> Create your first rule
            </Button>
          </div>
        ) : (
          <div>
            {rules.map(r => (
              <RuleRow
                key={r.id}
                rule={r}
                onEdit={() => { setEditing(r); setEditorOpen(true); }}
                onToggle={handleToggle}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </section>

      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <div className="font-semibold">Heads up — rules are advisory.</div>
          <p>
            Rules derive secondary values like cable sizes and notes — they don&apos;t overwrite anything the AI extracted from the breaker label or anything the engineer types into the primary fields. A qualified engineer must still verify every certificate before it&apos;s issued.
          </p>
        </div>
      </div>

      <RuleEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        initial={editing}
        onSaved={async () => { setEditorOpen(false); await load(); }}
      />
    </div>
  );
};

export default CertRules;
